import { DashboardLayout } from '@/components/layouts/dashboard-layout';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import { useCrisp } from '@/hooks/useCrisp';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { DisabledUserView } from '@/views/auth/DisabledUserView';
import { ForgotPasswordView } from '@/views/auth/ForgotPasswordView';
import { InvitationView } from '@/views/auth/InvitationView';
import { LoginView } from '@/views/auth/login';
import { ResetPasswordView } from '@/views/auth/ResetPasswordView';
import { VerifyMfaView } from '@/views/auth/VerifyMfaView';
import { CommandLineView } from '@/views/commander/CommandLineView';
import { CommandSimView } from '@/views/CommandSimView';
import { TermsView } from '@/views/terms/terms';
import * as Sentry from '@sentry/react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AdminRoute } from './components/AdminRoute';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { ViewerRestrictedRoute } from './components/ViewerRestrictedRoute';

import { BillingRunView } from './views/admin/BillingRunView';
import { BillingView } from './views/admin/BillingView';
import { CatalogManagementView } from './views/admin/CatalogManagementView';
import { DeviceModelDetailView } from './views/admin/DeviceModelDetailView';
import { SyncMonitoringView } from './views/admin/SyncMonitoringView';
import { AdminOrderDetailView } from './views/AdminOrderDetailView';
import { AdminOrderView } from './views/AdminOrderView';
import { AllOrganizationsView } from './views/AllOrganizationsView';
import { AnalyticsView } from './views/AnalyticsView';
import { CatalogView } from './views/CatalogView';
import { ConsumptionAlertsView } from './views/ConsumptionAlertsView';
import { HomeView } from './views/HomeView';
import InvoicesView from './views/InvoicesView';
import { LineDetailsView } from './views/LineDetailsView';
import { LinesView } from './views/LinesView';
import { TravelEsimsView } from './views/TravelEsimsView';
import { TravelEsimDetailView } from './views/TravelEsimDetailView';
import { OnboardingView } from './views/OnboardingView';
import { OrderConfirmationView } from './views/OrderConfirmationView';
import { OrderDetailView } from './views/OrderDetailView';
import { OrdersView } from './views/OrdersView';
import { OrganizationManagementView } from './views/OrganizationManagementView';
import { PlanPricesView } from './views/PlanPricesView';
import { SimStockView } from './views/SimStockView';
import { NotFoundView } from './views/NotFoundView';

// Activer/désactiver le mode maintenance temporaire
// Mettre à false pour désactiver le message d'avertissement
const MAINTENANCE_MODE = false;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// New component: UnauthenticatedRoute
// Only allows access if user is NOT authenticated
const UnauthenticatedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Root redirect component that uses the tenant slug
const RootRedirect = () => {
  const { tenantSlug } = useTenantSlug();

  if (!tenantSlug) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={`/o/${tenantSlug}/home`} replace />;
};

function App() {
  return (
    <Router>
      <Sentry.ErrorBoundary fallback={<p>Une erreur est survenue</p>}>
        <AppContent />
      </Sentry.ErrorBoundary>
    </Router>
  );
}

function AppContent() {
  useCrisp(); // This will handle Crisp visibility based on route

  // Afficher le message de maintenance si activé
  if (MAINTENANCE_MODE) {
    return <MaintenanceBanner />;
  }

  return (
    <>
      <Routes>
        {/* Public routes for unauthenticated users only */}
        <Route
          path="/login"
          element={
            <UnauthenticatedRoute>
              <LoginView />
            </UnauthenticatedRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <UnauthenticatedRoute>
              <ForgotPasswordView />
            </UnauthenticatedRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <UnauthenticatedRoute>
              <ResetPasswordView />
            </UnauthenticatedRoute>
          }
        />
        <Route
          path="/verify-mfa"
          element={
            <UnauthenticatedRoute>
              <VerifyMfaView />
            </UnauthenticatedRoute>
          }
        />
        <Route
          path="/invitations/:invitationId"
          element={
            <UnauthenticatedRoute>
              <InvitationView />
            </UnauthenticatedRoute>
          }
        />
        <Route path="/disabled-user" element={<DisabledUserView />} />
        <Route path="/terms" element={<TermsView />} />
        <Route path="/404" element={<NotFoundView />} />

        {/* Protected routes - with dashboard layout */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/o/:tenantSlug/home" element={<HomeView />} />
          <Route path="/o/:tenantSlug/lines" element={<LinesView />} />
          <Route
            path="/o/:tenantSlug/lines/:lineId"
            element={<LineDetailsView />}
          />
          <Route
            path="/o/:tenantSlug/travel-esims"
            element={<TravelEsimsView />}
          />
          <Route
            path="/o/:tenantSlug/travel-esims/:itemId"
            element={<TravelEsimDetailView />}
          />
          <Route path="/o/:tenantSlug/sim-stock" element={<SimStockView />} />
          <Route
            path="/o/:tenantSlug/consumption-alerts"
            element={<ConsumptionAlertsView />}
          />
          <Route
            path="/o/:tenantSlug/sim-stock/order"
            element={<CommandSimView />}
          />
          <Route path="/o/:tenantSlug/analytics" element={<AnalyticsView />} />
          <Route path="/o/:tenantSlug/orders" element={<OrdersView />} />
          <Route
            path="/o/:tenantSlug/orders/:orderId"
            element={<OrderDetailView />}
          />
          <Route
            path="/o/:tenantSlug/orders/:orderId/confirmation"
            element={<OrderConfirmationView />}
          />
          <Route
            path="/o/:tenantSlug/catalog"
            element={
              <ViewerRestrictedRoute>
                <CatalogView />
              </ViewerRestrictedRoute>
            }
          />
          <Route path="/o/:tenantSlug/invoices" element={<InvoicesView />} />
          <Route
            path="/o/:tenantSlug/manage"
            element={
              <ProtectedRoute>
                <ViewerRestrictedRoute>
                  <OrganizationManagementView />
                </ViewerRestrictedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/o/:tenantSlug/lines/new"
            element={<CommandLineView />}
          />
          <Route
            path="/o/admin/onboarding"
            element={
              <AdminRoute>
                <OnboardingView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/"
            element={
              <AdminRoute>
                <AllOrganizationsView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/orders"
            element={
              <AdminRoute>
                <AdminOrderView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/orders/:orderId"
            element={
              <AdminRoute>
                <AdminOrderDetailView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/:organizationId/plans"
            element={
              <AdminRoute>
                <PlanPricesView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/catalog"
            element={
              <AdminRoute>
                <CatalogManagementView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/device-models/:id"
            element={
              <AdminRoute>
                <DeviceModelDetailView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/sync"
            element={
              <AdminRoute>
                <SyncMonitoringView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/billing"
            element={
              <AdminRoute>
                <BillingView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/billing/:runId"
            element={
              <AdminRoute>
                <BillingRunView />
              </AdminRoute>
            }
          />
          <Route
            path="/o/admin/billing/:runId/:step"
            element={
              <AdminRoute>
                <BillingRunView />
              </AdminRoute>
            }
          />
        </Route>

        {/* Redirect root based on auth status */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />

        {/* Catch-all route for unknown paths - redirect to 404 page */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <NotFoundView />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </>
  );
}

export default App;
